import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Report } from 'organisms/CloudOpsReportAdmin/types';
import { collectEligibleReportIds } from 'organisms/CloudOpsReportAdmin/bulk/api';
import { ProjectReportsQuery } from 'organisms/CloudOpsReportAdmin/bulk/types';

interface Options {
  query: ProjectReportsQuery;
  resetKey: string;
  collect?: (query: ProjectReportsQuery) => Promise<number[]>;
}

export function useReportBulkSelection({
  query,
  resetKey,
  collect = collectEligibleReportIds,
}: Options) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);
  const collectingRef = useRef(false);

  const clearSelection = useCallback(() => {
    requestGenerationRef.current += 1;
    collectingRef.current = false;
    setSelectedIds([]);
    setAllMatchingSelected(false);
    setCollecting(false);
    setSelectionError(null);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, resetKey]);

  useEffect(
    () => () => {
      requestGenerationRef.current += 1;
      collectingRef.current = false;
    },
    []
  );

  const toggleReport = useCallback(
    (report: Pick<Report, 'id' | 'invoice_number'>) => {
      if (report.invoice_number || collectingRef.current) return;
      setSelectionError(null);
      setAllMatchingSelected(false);
      setSelectedIds((current) =>
        current.includes(report.id)
          ? current.filter((id) => id !== report.id)
          : [...current, report.id]
      );
    },
    []
  );

  const toggleAllMatching = useCallback(async () => {
    if (collectingRef.current) return;
    if (allMatchingSelected) {
      clearSelection();
      return;
    }

    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    collectingRef.current = true;
    setCollecting(true);
    setSelectionError(null);
    try {
      const ids = await collect(query);
      if (requestGenerationRef.current !== requestGeneration) return;
      setSelectedIds(ids);
      setAllMatchingSelected(ids.length > 0);
      if (ids.length === 0) {
        setSelectionError('No eligible reports were found.');
      }
    } catch (error) {
      if (requestGenerationRef.current !== requestGeneration) return;
      setSelectionError('Failed to select eligible reports.');
    } finally {
      if (requestGenerationRef.current === requestGeneration) {
        collectingRef.current = false;
        setCollecting(false);
      }
    }
  }, [allMatchingSelected, clearSelection, collect, query]);

  return useMemo(
    () => ({
      selectedIds,
      selectedCount: selectedIds.length,
      allMatchingSelected,
      someSelected: selectedIds.length > 0 && !allMatchingSelected,
      collecting,
      selectionError,
      isSelected: (id: number) => selectedIds.includes(id),
      toggleReport,
      toggleAllMatching,
      clearSelection,
    }),
    [
      allMatchingSelected,
      clearSelection,
      collecting,
      selectedIds,
      selectionError,
      toggleAllMatching,
      toggleReport,
    ]
  );
}
